import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ZohoIntegrationActions from '@Actions/integrations/zohoIntegrationActions';
import {
  Button, Checkbox, Alert, notification,
} from 'antd';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import H3Text from '@Uilib/h3Text';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import SelectDepartment from '@Components/Common/SelectDepartment';
import './style.scss';
import ScheduledReport from '../../../../../../ReportsV2/ScheduledReport';
import ZohoPullTenantMapping from './ZohoPullTenantMapping';
import documentUINamesConstant from '../../../../../../../apis/documentUINamesConstant';

/**
 *
 */
function ZohoPullData({
  pullProducts, pullVendors, user, setStep, pullCustomers, zohoAutomaticDocumentSync, preFillData,
}) {
  const [isPullProductEnabled, setIsPullProductEnabled] = useState(true);
  const [isPullVendorEnabled, setIsPullVendorEnabled] = useState(true);
  const [isPullCustomerEnabled, setIsPullCustomerEnabled] = useState(true);
  const [isPullSalesOrderEnabled, setIsPullSalesOrderEnabled] = useState(true);
  const [isPushBillsEnabled, setIsPushBillsEnabled] = useState(true);
  const [isPushInvoicesEnabled, setIsPushInvoicesEnabled] = useState(true);
  const [isPullVendorPaymentsEnabled, setIsPullVendorPaymentsEnabled] = useState(true);
  const [isPullCustomerPaymentsEnabled, setIsPullCustomerPaymentsEnabled] = useState(true);
  const [isAutomaticSyncEnabled, setIsAutomaticSyncEnabled] = useState(true);
  const [scheduleData, setScheduleData] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [zohoPullTenantMapping, setZohoPullTenantMapping] = useState();

  const documentUINames = useMemo(() => documentUINamesConstant(user), [user]);

  useEffect(() => {
    if (preFillData) {
      setIsPushBillsEnabled(preFillData.push_bills_automatically);
      setIsPushInvoicesEnabled(preFillData.push_invoices_automatically);
      setIsPullSalesOrderEnabled(preFillData.pull_sales_orders_automatically);
      setIsPullVendorPaymentsEnabled(preFillData.pull_vendor_payments_automatically);
      setIsPullCustomerPaymentsEnabled(preFillData.pull_customer_payments_automatically);
      setIsAutomaticSyncEnabled(true);
      setScheduleData(preFillData.schedule_configurations);
    }
  }, [preFillData]);
  const handleSubmit = () => {
    setFormSubmitted(true);
    if (isAutomaticSyncEnabled && isPullSalesOrderEnabled && !zohoPullTenantMapping?.length) {
      notification.open({
        type: 'error',
        // message: ' Branch mapping are required when Sales Order Sync are enabled.',
        message: `Branch mapping are required when ${documentUINames.salesOrderUIName} Sync are enabled.`,
        duration: 4,
        placement: 'top',
      });
      return; // Stop further execution if validation fails
    }

    const payload = {
      org_id: user?.tenant_info?.org_id,
      per_page: 10000,
    };

    const automaticSyncPayload = {
      org_id: user?.tenant_info?.org_id,
      schedule_configurations: {
        selectedTime: scheduleData?.selectedTime,
        selectedFrequency: scheduleData?.selectedFrequency,
        selectedDays: scheduleData?.selectedDays,
        selectedDate: scheduleData?.selectedDate ? scheduleData?.selectedFrequency === 'MONTHLY' ? scheduleData?.selectedDate.date() : dayjs(scheduleData?.selectedDate).format('DD-MM-YYYY') : null,
      },
      pull_zoho_location_mapping: zohoPullTenantMapping,
      copy_schedule_configurations: scheduleData,
      sync_documents_automatically: isAutomaticSyncEnabled || false,
      push_bills_automatically: isAutomaticSyncEnabled ? isPushBillsEnabled : false,
      push_invoices_automatically: isAutomaticSyncEnabled ? isPushInvoicesEnabled : false,
      pull_sales_orders_automatically: isAutomaticSyncEnabled ? isPullSalesOrderEnabled : false,
      pull_vendor_payments_automatically: isAutomaticSyncEnabled ? isPullVendorPaymentsEnabled : false,
      pull_customer_payments_automatically: isAutomaticSyncEnabled ? isPullCustomerPaymentsEnabled : false,
      user_id: user?.user_id,
    };
    if (isAutomaticSyncEnabled ? !scheduleData.isValid : false) {
      notification.open({
        type: 'error',
        message: `These fields are required: ${scheduleData?.missingFields.join(', ')}`,
        duration: 4,
        placement: 'top',
      });
    } else if (isPullProductEnabled || isPullVendorEnabled || isPullCustomerEnabled || isAutomaticSyncEnabled) {
      notification.open({
        type: 'success',
        message: 'Please wait while we are pulling your requested data from Zoho Books. This might take up to 30 minutes to complete.',
        duration: 6,
        placement: 'top',
      });
      if (isPullProductEnabled) {
        pullProducts(payload);
      }
      if (isPullVendorEnabled) {
        pullVendors(payload);
      }
      if (isPullCustomerEnabled) {
        pullCustomers(payload);
      }
      if (isAutomaticSyncEnabled) {
        zohoAutomaticDocumentSync(automaticSyncPayload);
      }
      setStep(3);
    }
  };

  const getHeading = () => 'Please choose the entities that needs to be imported from Zoho';
  return (
    <div className="zoho__sync-wrapper">
      <div className="ant-row">
        <div className="ant-col-md-24">
          <div className="zoho__sync-heading">
            {getHeading()}
          </div>
        </div>
        <div className="ant-col-md-18">
          <div className="zoho__sync-warning-message">
            <Alert showIcon type="warning" message="This is a one time process, updates on these records in zoho will not be captured on Procuzy." />
          </div>
        </div>
        <div className="ant-col-md-24">
          <div className="zoho__sync-checkbox-wrapper">
            <div className="zoho__sync-checkbox">
              <Checkbox checked={isPullProductEnabled} onChange={(e) => setIsPullProductEnabled(e.target.checked)}>
                Yes, Pull items from Zoho to Procuzy
              </Checkbox>
            </div>
            <div className="zoho__sync-checkbox">
              <Checkbox checked={isPullCustomerEnabled} onChange={(e) => setIsPullCustomerEnabled(e.target.checked)}>
                Yes, Pull customers from Zoho to Procuzy
              </Checkbox>
            </div>
            <div className="zoho__sync-checkbox">
              <Checkbox checked={isPullVendorEnabled} onChange={(e) => setIsPullVendorEnabled(e.target.checked)}>
                Yes, Pull vendors from Zoho to Procuzy
              </Checkbox>
            </div>
            <div className="zoho__sync-checkbox">
              <Checkbox checked={isAutomaticSyncEnabled} onChange={(e) => setIsAutomaticSyncEnabled(e.target.checked)}>
                Setup automatic Sync with zoho
              </Checkbox>
            </div>
            {isAutomaticSyncEnabled && (
              <div className="zoho__sync-automatic">
                <div className="zoho__sync-left">
                  <div className="zoho__sync-checkbox">
                    <Checkbox checked={isPushBillsEnabled} onChange={(e) => setIsPushBillsEnabled(e.target.checked)}>
                      Yes, Push bills automatically from procuzy to zoho
                    </Checkbox>
                  </div>
                  <div className="zoho__sync-checkbox">
                    <Checkbox checked={isPushInvoicesEnabled} onChange={(e) => setIsPushInvoicesEnabled(e.target.checked)}>
                      Yes, Push invoices automatically from procuzy to zoho
                    </Checkbox>
                  </div>
                  <div className="zoho__sync-checkbox">
                    <Checkbox checked={isPullSalesOrderEnabled} onChange={(e) => setIsPullSalesOrderEnabled(e.target.checked)}>
                      {/* Yes, Pull sales order automatically from procuzy to zoho */}
                      {`Yes, Pull ${documentUINames.salesOrderUIName.toLowerCase()} automatically from procuzy to zoho`}
                    </Checkbox>
                  </div>
                </div>
                <div className="zoho__sync-right">
                  <div className="zoho__sync-checkbox">
                    <Checkbox checked={isPullVendorPaymentsEnabled} onChange={(e) => setIsPullVendorPaymentsEnabled(e.target.checked)}>
                      Yes, Pull vendor payments automatically from procuzy to zoho
                    </Checkbox>
                  </div>
                  <div className="zoho__sync-checkbox">
                    <Checkbox checked={isPullCustomerPaymentsEnabled} onChange={(e) => setIsPullCustomerPaymentsEnabled(e.target.checked)}>
                      Yes, Pull customer payments automatically from procuzy to zoho
                    </Checkbox>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <ScheduledReport
                    allowedDurations={['DAILY', 'HOURLY', 'WEEKLY', 'MONTHLY']}
                    setScheduledReportData={setScheduleData}
                    schedulePrefillData={scheduleData}
                    alwaysOn
                  />
                  <div style={{ width: '300px' }} />
                </div>
                <ZohoPullTenantMapping
                  setZohoPullTenantMapping={setZohoPullTenantMapping}
                  preFillData={preFillData}
                />
              </div>
            )}
          </div>
        </div>
        <div className="ant-row">
          <H3Button
            text="Back"
            buttonType={defaultButtonTypes.BLUE_BORDERED}
            onClick={() => setStep(1)}
            customClass="zoho__sync-wrapper-submit"
          />
          &nbsp;&nbsp;
          <H3Button
            text="Next Step"
            buttonType={defaultButtonTypes.BLUE_ROUNDED}
            onClick={() => handleSubmit()}
            customClass="zoho__sync-wrapper-submit"
          />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({ ZohoIntegrationReducers, UserReducers }) => ({
  pullProductsLoading: ZohoIntegrationReducers.pullProductsLoading,
  pullVendorsLoading: ZohoIntegrationReducers.pullVendorsLoading,
  pullCustomersLoading: ZohoIntegrationReducers.pullCustomersLoading,
  user: UserReducers.user,
});
const mapDispatchToProps = (dispatch) => ({
  pullProducts: (payload, callback) => dispatch(ZohoIntegrationActions.pullProducts(payload, callback)),
  pullVendors: (payload, callback) => dispatch(ZohoIntegrationActions.pullVendors(payload, callback)),
  pullCustomers: (payload, callback) => dispatch(ZohoIntegrationActions.pullCustomers(payload, callback)),
  zohoAutomaticDocumentSync: (payload, callback) => dispatch(ZohoIntegrationActions.zohoAutomaticDocumentSync(payload, callback)),
});
ZohoPullData.propTypes = {
  user: PropTypes.any,
  pullProducts: PropTypes.func,
  pullProductsLoading: PropTypes.bool,
  pullVendors: PropTypes.func,
  pullVendorsLoading: PropTypes.bool,
  pullCustomers: PropTypes.func,
  pullCustomersLoading: PropTypes.bool,
  setStep: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ZohoPullData));
